import React, { useEffect, useState } from "react";
import { Routes, Route, useNavigate, Link } from "react-router-dom";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./lib/supabase";
import Dashboard from "./pages/Dashboard";
import Comparison from "./pages/Comparison";
import NewLanding from "./pages/NewLanding"; // ✅ landing page component

// --- simple inline styles used by debug/auth wrappers ---
const box: React.CSSProperties = {
  maxWidth: 640,
  margin: "80px auto",
  padding: 24,
  textAlign: "center",
};
const btn: React.CSSProperties = {
  padding: "10px 16px",
  borderRadius: 6,
  border: "none",
  cursor: "pointer",
};

// --- optional debug widget for env vars ---
function DebugPanel() {
  const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const anon = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
  const missing = !url || !anon;
  return (
    <div
      style={{
        position: "fixed",
        bottom: 12,
        left: 12,
        fontSize: 12,
        background: "#111",
        color: "#fff",
        padding: "8px 10px",
        borderRadius: 6,
        opacity: 0.8,
      }}
    >
      <div>Debug:</div>
      <div>VITE_SUPABASE_URL: {url ? "✅ set" : "❌ missing"}</div>
      <div>VITE_SUPABASE_ANON_KEY: {anon ? "✅ set" : "❌ missing"}</div>
      {missing && (
        <div style={{ marginTop: 4, color: "#ffb3b3" }}>
          Auth UI will still show; set envs in Netlify to enable Google sign-in.
        </div>
      )}
    </div>
  );
}

// --- login wrapper (unchanged, reachable at /auth now) ---
function AuthWrapper() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const canAuth =
    Boolean(import.meta.env.VITE_SUPABASE_URL) &&
    Boolean(import.meta.env.VITE_SUPABASE_ANON_KEY);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (!active) return;
        if (error) console.warn("supabase.auth.getSession error:", error);
        setSession(data?.session ?? null);
      } catch (err) {
        console.warn("getSession threw:", err);
      } finally {
        if (active) setLoading(false);
      }
    })();

    const { data } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      if (newSession) navigate("/dashboard", { replace: true });
      else navigate("/auth", { replace: true });
    });

    return () => {
      active = false;
      data?.subscription?.unsubscribe?.();
      // @ts-ignore
      data?.unsubscribe?.();
    };
  }, [navigate]);

  if (loading)
    return (
      <div style={box}>
        <h2>Loading…</h2>
      </div>
    );

  if (!session)
    return (
      <div style={box}>
        <h1>🚀 Universal AI Cold Calling CRM</h1>
        <p>Sign in to get started.</p>
        <div
          style={{
            marginTop: 20,
            display: "flex",
            gap: 12,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <button
            style={{ ...btn, background: "#4285F4", color: "#fff" }}
            onClick={() => {
              if (!canAuth) {
                alert(
                  "Missing Supabase env vars. Add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY."
                );
                return;
              }
              supabase.auth.signInWithOAuth({ provider: "google" });
            }}
          >
            Sign in with Google
          </button>

          <Link to="/dashboard">
            <button style={{ ...btn, background: "#eee" }}>
              Continue without auth (debug)
            </button>
          </Link>
        </div>
        <DebugPanel />
      </div>
    );

  return (
    <div style={box}>
      <h1>Welcome, {session.user.email} 👋</h1>
      <p>You’re now connected to Supabase successfully!</p>
      <div style={{ marginTop: 16 }}>
        <Link to="/dashboard">
          <button style={{ ...btn, background: "#eee", marginRight: 8 }}>
            Go to Dashboard
          </button>
        </Link>
        <button
          style={{ ...btn, background: "crimson", color: "#fff" }}
          onClick={() => supabase.auth.signOut()}
        >
          Sign Out
        </button>
      </div>
      <DebugPanel />
    </div>
  );
}

// --- simple /health route ---
function Health() {
  return (
    <div style={box}>
      <h1>✅ Health</h1>
      <p>App rendered. Router working.</p>
      <p>
        <Link to="/">Go Home</Link>
      </p>
      <DebugPanel />
    </div>
  );
}

// --- router configuration ---
export default function App() {
  return (
    <Routes>
      {/* new marketing homepage */}
      <Route path="/" element={<NewLanding />} />

      {/* old auth screen accessible at /auth */}
      <Route path="/auth" element={<AuthWrapper />} />

      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/comparison" element={<Comparison />} />
      <Route path="/health" element={<Health />} />
      {/* fallback */}
      <Route path="*" element={<NewLanding />} />
    </Routes>
  );
}
